import type { HTMLAttributes, ReactNode } from 'react';
import type { SlaState, TicketStatus, UserConfirmationStatus } from '@ithelpdesk/shared';
import { cx } from '../cx';
import { statusMeta, confirmationMeta, slaMeta, type Shape, type Tone } from '../status';
import { Tooltip } from '../Tooltip/Tooltip';
import styles from './StatusBadge.module.css';

type BaseProps = Omit<HTMLAttributes<HTMLSpanElement>, 'children'> & {
  /**
   * `default`: bordered chip with a shape glyph (detail header, mini-lists).
   * `soft`: borderless soft-filled pill, no glyph — dense ticket tables, where
   * the always-visible text label carries the meaning.
   */
  variant?: 'default' | 'soft';
};

export type StatusBadgeProps = BaseProps &
  ({ status: TicketStatus } | { confirmation: UserConfirmationStatus } | { sla: SlaState });

function isConfirmationProps(props: StatusBadgeProps): props is BaseProps & { confirmation: UserConfirmationStatus } {
  return 'confirmation' in props;
}

function isSlaProps(props: StatusBadgeProps): props is BaseProps & { sla: SlaState } {
  return 'sla' in props;
}

/**
 * Renders either a ticket lifecycle status or a user-confirmation status as a
 * shape + text badge. Status is never conveyed by color alone: the label is
 * always visible text, and the shape is a second, non-color channel.
 *
 * The confirmation wording is exactly the language mandated for the "resolved
 * is not closed" lifecycle: AUTO_CONFIRMED and ADMIN_OVERRIDE never read as
 * the requester having confirmed anything, and both carry a tooltip spelling
 * out why.
 */
export function StatusBadge(props: StatusBadgeProps) {
  if (isConfirmationProps(props)) {
    const { confirmation, className, variant, ...rest } = props;
    const meta = confirmationMeta(confirmation);
    return (
      <Chip label={meta.label} tone={meta.tone} shape="circle" tooltip={meta.tooltip} className={className} variant={variant} rest={rest} />
    );
  }
  if (isSlaProps(props)) {
    const { sla, className, variant, ...rest } = props;
    const meta = slaMeta(sla);
    return <Chip label={meta.label} tone={meta.tone} shape="diamond" className={className} variant={variant} rest={rest} />;
  }
  const { status, className, variant, ...rest } = props;
  const meta = statusMeta(status);
  return (
    <Chip
      label={meta.label}
      tone={meta.tone}
      shape={meta.shape}
      tooltip={meta.description}
      className={className}
      variant={variant}
      rest={rest}
    />
  );
}

interface ChipProps {
  label: string;
  tone: Tone;
  shape: Shape;
  tooltip?: string | undefined;
  className?: string | undefined;
  variant?: 'default' | 'soft' | undefined;
  rest: Omit<HTMLAttributes<HTMLSpanElement>, 'children' | 'className'>;
}

function Chip({ label, tone, shape, tooltip, className, variant = 'default', rest }: ChipProps): ReactNode {
  const soft = variant === 'soft';
  const badge = (
    <span className={cx(styles.badge, styles[tone], soft && styles.soft, className)} tabIndex={tooltip ? 0 : undefined} {...rest}>
      {!soft && <span className={cx(styles.shape, styles[shape])} aria-hidden="true" />}
      {label}
    </span>
  );
  if (!tooltip) return badge;
  return <Tooltip content={tooltip}>{badge}</Tooltip>;
}
